<template>
  <div>
    <Section>
      <SectionHeader :title="$t('apps.inventory.message.general')"/>
      <FormRow>
        <Col className="col-lg-4">
        <Label :text="`${$t('apps.inventory.label.warehousename')} *`" />
        <BaseInput
          v-model="$v.input.name.$model"
          :classNames="[{ 'is-invalid': $v.input.name.$error }]"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.product.message.deductstockfrom')"/>
      <!-- {{ optionSkus }}
      <br><hr> -->
      <!-- {{ input.stockSkus }}
      <br><hr> -->
      <!-- {{ selectedSkuIds }}
      <br><hr> -->
      <!-- <pre>{{ input }}</pre> -->
      <FormRow
        v-for="(item, index) in input.stockSkus"
        :key="index"
        class="mb-2 form-control-mobile">
        <div class="col-lg-4 mb-md-2 mb-2">
          <vue-bootstrap-autocomplete
            v-model="item.query_product_name"
            :data="item.sku_id ? [] : optionSkus.filter((optionSkuItem) => !selectedSkuIds.includes(optionSkuItem.value))"
            :serializer="(item) => item.text + ' ' + (item.sku_code || '')"
            :minMatchingChars="1"
            :maxMatches="10"
            :placeholder="$t('placeholder.selectaproduct')"
            :loading="item.loading"
            :state="$v.input.stockSkus.$each[index].sku_id.$error ? false : null"
            :disabled="disabled"
            :showOnFocus="true"
            :noResultsInfo="!item.sku_id ? $t('message.notfounddata') : null"
            :showCreate="false"
            @hit="handleSelectProduct(index, $event)"
            @blur="handleBlurProduct(index, $event)"
            @inputBlur="handleInputBlurProduct(index, $event)"
            @keydown="handleKeydownProduct(index, $event)"
            @keyup="handleKeyupProduct(index, $event)"
          >
            <template
              slot="suggestion"
              slot-scope="{ data, htmlText }">
              <div class="d-flex align-items-center">
                <img
                  :src="productImage(data.value)"
                  style="width: 40px; height: 40px; border-radius: 0.25rem;"
                >
                <div class="d-flex flex-column">
                  <span
                    class="ml-4"
                    v-html="data.text"/>
                  <span
                    class="ml-4"
                    style="font-size: 11px;"
                    v-html="data.sku_code"/>
                </div>
              </div>
            </template>
          </vue-bootstrap-autocomplete>
        </div>
        <div class="col-lg-4 mb-md-2 mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text">
                {{ $t('apps.product.message.warehouselocation') }}
              </span>
            </div>
            <select
              v-model="item.location_id"
              :disabled="!item.sku_id || disabled"
              :class="[
                'form-control custom-select',
                $v.input.stockSkus.$each[index].location_id.$error && 'is-invalid'
              ]"
              @change="handleSelectLocationChange($event.target.value, index)"
            >
              <option :value="null">
                {{ $t('placeholder.select') }}
              </option>
              <option
                v-for="(locationItem, index) in getLocationList(getSkuInfoBySkuId(item.sku_id))"
                :key="index"
                :value="locationItem.value"
              >
                {{ locationItem.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-lg-3 mb-md-2 mb-2">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text">
                {{ $t('apps.product.message.requiredunit') }}
              </span>
            </div>
            <input
              v-model="item.required_unit"
              :disabled="disabled || !item.sku_id"
              type="text"
              class="form-control text-right number-only text-select-all"
              placeholder=""
              @keypress="numberOnly"
              @keyup="item.required_unit = setToValidNumber(item.required_unit)"
              @blur="item.required_unit = setToCustomDigitsNumber(item.required_unit)">
          </div>
        </div>
        <div
          v-if="!disabled"
          class="col-lg-1 text-center">
          <button
            class="btn btn-light col-12"
            @click="deleteStockSku(index)">
            <i class="far fa-trash-alt"/>
          </button>
        </div>
      </FormRow>
      <FormRow v-if="!disabled">
        <div class="col-lg-6">
          <BaseButton
            className="btn-light"
            @click="addStockSku()"
          >
            {{ $t('button.addproduct') }}
          </BaseButton>
        </div>
      </FormRow>
    </Section>

    <Section class="mt-3">
      <SectionHeader :title="$t('message.assignitems')"/>
      <FormRow>
        <Col className="col-lg-12">
        <BaseNotFoundData
          v-if="!products.length"
          :title="$t('apps.inventory.message.notfoundskus')"
          :detail="$t('apps.inventory.message.createnewproducts')"
        />
        <BaseAssignSkus
          v-else
          v-model="input.assignSkus"
          :items="assignItems"
          :disabled="disabled"
          :disabledItemMessage="false"
        />
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      v-if="showActionButtons"
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onSubmit="handleSubmit"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'
import { cloneDeep, debounce } from 'lodash'

export default {
  props: {
    defaultValues: Object,
    isSubmitted: Boolean,
    disabled: Boolean,
    products: {
      type: Array,
      default: () => ([]),
    },
    warehouses: {
      type: Array,
      default: () => ([]),
    },
    showActionButtons: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      input: {
        name: null,
        stockSkus: [],
        assignSkus: [],
      },
      optionSkus: [],
      productsAvailable: [],
    }
  },

  validations: {
    input: {
      name: {
        required,
      },
      stockSkus: {
        required,
        minLength: minLength(1),
        $each: {
          sku_id: {
            required: function (value) {
              const filledOne = this.input.stockSkus.some((item) => item.sku_id)
              if (filledOne) {
                return true
              }
              return !!value
            },
          },
          location_id: {
            required: (value, item) => {
              if (item.sku_id) {
                return !!item.location_id
              }
              return true
            },
          },
        },
      },
    },
  },

  computed: {
    ...mapGetters({
      getSkuInfoBySkuId: 'getSkuInfoBySkuId',
      getProductInfoBySkuId: 'getProductInfoBySkuId',
      defaultImages: 'getDefaultImages',
      getBinName: 'getBinName',
      hasStock: 'hasStock',
      hasStockIn: 'hasStockIn',
    }),
    assignItems () {
      return this.products.reduce((acc, cur) => {
        const skus = cur.prices.map((price) => {
          return {
            text: this.getProductFullName(cur.product_name, price.attributes),
            // textRight: 'xxx, xxx',
            value: price.sku_id,
            disabled: false,
          }
        })
        return acc.push(...skus) && acc
      }, [])
    },
    selectedSkuIds() {
      return this.input.stockSkus.map((stockSku) => stockSku.sku_id)
    },
  },

  watch: {
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    this.setProductsAvailable()

    if (this.defaultValues) {
      this.setInput(this.defaultValues)
      if (this.input.stockSkus.length === 0) {
        this.addStockSku()
        this.addStockSku()
      }
    } else {
      this.addStockSku()
      this.addStockSku()
    }
  },

  methods: {
    addStockSku() {
      this.input.stockSkus.push({
        sku_id: '',
        required_unit: this.setToCustomDigitsNumber(0),
        query_product_name: '',
        location_id: null,
        location_data: null,
        loading: false,
      })
    },
    deleteStockSku(index) {
      this.input.stockSkus.splice(index, 1)

      const that = this
      if (that.input.stockSkus.length == 0) {
        setTimeout(() => {
          that.addStockSku()
        }, 100)
      }
    },
    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToTop(600)
      } else {
        this.$emit('onSubmit', {
          ...this.input,
          stockSkus: this.input.stockSkus.filter((stockSkuItem) => stockSkuItem.sku_id),
        })
      }
    },
    setInput (defaultValues) {
      this.input.name = defaultValues.recipe.name
      this.input.stockSkus = defaultValues.recipe.stock_skus
        .filter((stockSkuItem) => {
          const skuInfo = this.getSkuInfoBySkuId(stockSkuItem.sku_id)
          return !!skuInfo
        })
        .map((stockSkuItem) => {
          const skuInfo = this.getSkuInfoBySkuId(stockSkuItem.sku_id)
          const productInfo = this.getProductInfoBySkuId(stockSkuItem.sku_id)
          const locationId = this.getLocationId({
            sku_id: stockSkuItem.sku_id,
            bin_location_id: stockSkuItem.bin_location_id,
            stock_id: stockSkuItem.stock_id,
            wh_id: stockSkuItem.wh_id,
          })
          return {
            sku_id: stockSkuItem.sku_id,
            required_unit: this.setToCustomDigitsNumber(stockSkuItem.required_unit),
            query_product_name: this.getProductFullName(productInfo.product_name, skuInfo.attributes),
            location_id: locationId,
            location_data: this.getStockSku(locationId),
            loading: false,
          }
        })
      this.input.assignSkus = defaultValues.recipe.assign_skus
        .filter((item) => {
          const productInfo = this.getProductInfoBySkuId(item.sku_id)
          return productInfo != null
        })
        .map((item) => {
          const skuInfo = this.getSkuInfoBySkuId(item.sku_id)
          const productInfo = this.getProductInfoBySkuId(item.sku_id)
          return {
            text: this.getProductFullName(productInfo.product_name, skuInfo.attributes),
            value: item.sku_id,
            active: true,
          }
        })
    },
    productImage (skuId) {
      if (!skuId) return this.defaultImages.product
      const product = this.getProductInfoBySkuId(skuId)
      return (product && product.images.thumbnail) || this.defaultImages.product
    },
    handleSelectProduct(index, event) {
      const skuId = event.value
      this.input.stockSkus[index].sku_id = skuId
      this.input.stockSkus[index].image = this.productImage(skuId)
      this.input.stockSkus[index].query_product_name = event.text
      this.input.stockSkus[index].loading = false

      const skuInfo = this.getSkuInfoBySkuId(skuId)
      const locations = this.getLocationList(skuInfo)
      if (locations.length === 1) {
        const locationId = locations[0].value
        this.input.stockSkus[index].location_id = locationId
        this.handleSelectLocationChange(locationId, index)
      }

      this.isLookupProductsDisabled = true
      setTimeout(() => {
        this.isLookupProductsDisabled = false
      }, 600)
    },
    handleInputBlurProduct(index, event) {
      const data = this.input.stockSkus[index]
      if (!data.sku_id && this.optionSkus.length === 0) {
        this.input.stockSkus[index].query_product_name = ''
      }
    },
    handleBlurProduct(index, event) {
      const data = this.input.stockSkus[index]
      if (!data.sku_id) {
        this.input.stockSkus[index].query_product_name = ''
      }
    },
    handleKeydownProduct(index, event) {
      if (this.input.stockSkus[index].sku_id) {
        event.preventDefault()
      }
    },
    handleKeyupProduct(index, event) {
      if (this.isLookupProductsDisabled) {
        return
      }

      if (event.key === 'Backspace' && this.input.stockSkus[index].sku_id) {
        this.input.stockSkus[index].query_product_name = ''
      }

      if (!this.input.stockSkus[index].query_product_name) {
        this.input.stockSkus[index].sku_id = ''
        this.input.stockSkus[index].location_id = null
        this.input.stockSkus[index].required_unit = this.setToCustomDigitsNumber(0)
        this.input.stockSkus[index].loading = false
      }
    },
    handleSelectLocationChange(locationId, index) {
      this.input.stockSkus[index].location_data = this.getStockSku(locationId)
    },
    setProductsAvailable () {
      this.productsAvailable = []
      for (let i in this.products) {
        let product = this.products[i]
        for (let j in product.prices) {
          let price = product.prices[j]
          const hasStocks = this.hasStock(price.sku_id)
          if (hasStocks) {
            this.productsAvailable.push({
              product_name: this.getProductFullName(product.product_name, price.attributes),
              sku_id: price.sku_id,
              price: price.price || 0,
              cost: price.cost || 0,
              barcode: price.barcode,
              unit: product.unit,
              config: price.config,
              stocks: price.stocks,
              sku_code: price.sku_code,
            })
          }
        }
      }
      for (let i in this.data) {
        const hasSku = this.data[i].sku_id
        const notExist = !this.productsAvailable.find((item) => item.sku_id === this.data[i].sku_id)
        if (hasSku && notExist) {
          this.productsAvailable.push({
            product_name: this.data[i].item,
            sku_id: this.data[i].sku_id,
            price: this.data[i].price || 0,
            cost: 0,
            unit: null,
            sku_code: this.data[i].sku_code,
          })
        }
      }
      this.setOptionSkus()
    },
    setOptionSkus () {
      this.optionSkus = []
      for (let i in this.productsAvailable) {
        let item = this.productsAvailable[i]
        this.optionSkus.push({
          text: item.product_name,
          sku_code: item.sku_code,
          value: item.sku_id,
        })
      }
    },
    getLocationList(skuInfo) {
      return !skuInfo ? [] : skuInfo.stocks.reduce((acc, cur) => {
        const addItem = (bin_location_id) => {
          const warehouseItem = this.warehouses.find((whItem) => whItem.warehouse_id === cur.warehouse_id)
          const binName = this.getBinName(bin_location_id)
          const hasStockIn = this.hasStockIn(skuInfo.sku_id, warehouseItem.warehouse_id)
          if (hasStockIn) {
            acc.push({
              wh_name: warehouseItem.warehouse_name,
              sku_id: skuInfo.sku_id,
              bin_location_id: bin_location_id,
              stock_id: cur.stock_id,
              wh_id: warehouseItem.warehouse_id,
              bin_name: binName,
              text: (() => {
                let str = warehouseItem.warehouse_name
                if (bin_location_id) {
                  str += ` - ${binName}`
                }
                return str
              })(),
              value: this.getLocationId({
                sku_id: cur.sku_id,
                bin_location_id: bin_location_id,
                stock_id: cur.stock_id,
                wh_id: cur.warehouse_id,
              }),
            })
          }
        }

        if (cur.stock_locations.length === 0) {
          addItem(null)
        } else {
          cur.stock_locations.forEach(locationItem => {
            addItem(locationItem.bin_location_id)
          })
        }
        return acc
      }, [])
    },
    getLocationId({ sku_id, bin_location_id, stock_id, wh_id }) {
      return `${sku_id}|${bin_location_id}|${stock_id}|${wh_id}`
    },
    getStockSku(locationId) {
      const arr = locationId.split('|')
      const ifNull = (value) => value == 'null' ? null : value
      return {
        sku_id: ifNull(arr[0]),
        bin_location_id: ifNull(arr[1]),
        stock_id: ifNull(arr[2]),
        wh_id: ifNull(arr[3]),
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/RecipeForm.vue